import { VendorTypeEnum } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import LogService from '@zola-helpers/client/dist/es/util/logService';
import { BulkCreateInquiryRequest, CreateInquiryRequest } from '@zola/svc-marketplace-ts-types';

import * as NotificationActions from '~/actions/notificationActions';
import { AppThunk } from '~/reducers';
import { MappedInquiryView } from '~/types/mappedResponseTypes';
import { MappedInquiryPreferencesView, InquiryServicesFacet } from '~/types/responseTypes';

import ApiService from '../util/apiService';
import Logger from '../util/logger';
import { requestLastInquiryToStorefront } from './couplesInquiriesActions';
import { receivedLastInquiryToStorefront } from './types/couplesInquiriesActionTypes';
import {
  ReceivedInquiryPreferencesAction,
  RECEIVED_INQUIRY_PREFERENCES,
  ReceivedInquiryServicesAction,
  RequestingInquiryPreferencesAction,
  REQUESTING_INQUIRY_PREFERENCES,
} from './types/inquiryActionTypes';
import { showExistingInquiryError } from './util/showExistingInquiryError';

function requestingInquiryPreferences(): RequestingInquiryPreferencesAction {
  return {
    type: REQUESTING_INQUIRY_PREFERENCES,
  };
}

export function receivedInquiryPreferences(
  vendorType: VendorTypeEnum,
  preferences: MappedInquiryPreferencesView
): ReceivedInquiryPreferencesAction {
  return {
    type: RECEIVED_INQUIRY_PREFERENCES,
    payload: {
      vendorType,
      preferences,
    },
  };
}

// gets data to pre-fill couple-facing vendor inquiry form.
// the data is based on the last inquiry of the same type if
// it exists, or otherwise based on info the user has entered in
// an onboarding flow (the user's account_wedding_preferences and
// account_vendor_preferences).
export function getInquiryPreferences(
  vendorType: VendorTypeEnum
): AppThunk<Promise<MappedInquiryPreferencesView>> {
  return (dispatch) => {
    dispatch(requestingInquiryPreferences());
    return ApiService.get<MappedInquiryPreferencesView>(
      `/web-marketplace-api/v1/inquiry/preferences?vendor_type=${vendorType}`
    )
      .then((response) => {
        dispatch(receivedInquiryPreferences(vendorType, response));
        return response;
      })
      .catch((error) => {
        Logger.error(error.message, error);
        dispatch(NotificationActions.error({ message: 'Error receiving inquiry preferences' }));
        throw error;
      });
  };
}

// gets data to pre-fill vendor services that the vendor actually offers in inquiry form.
export const getInquiryServicesFromEndpoint = (uuid: string): AppThunk<Promise<void>> => {
  return (dispatch) => {
    return ApiService.get(`/web-marketplace-api/v1/storefront/${uuid}/options`)
      .then((response) => {
        const services: InquiryServicesFacet[] = response.filter(
          (i: InquiryServicesFacet) => i.includeInInquiry === true
        );
        const payload = { uuid, services };
        dispatch(ReceivedInquiryServicesAction(payload));
      })
      .catch((error) => {
        Logger.error(error.message, error);
        dispatch(NotificationActions.error({ message: 'Error receiving inquiry services' }));
      });
  };
};

export const ALREADY_INQUIRED_MESSAGE = "You've already inquired with this vendor";
export function createInquiry(
  createInquiryRequest: CreateInquiryRequest,
  storefrontUuid: string
): AppThunk<Promise<MappedInquiryView>> {
  return (dispatch) =>
    dispatch(requestLastInquiryToStorefront(storefrontUuid)).then((existingInquiry) => {
      if (!existingInquiry) {
        return ApiService.post(
          `/web-marketplace-api/v1/inquiry/for-storefront/${storefrontUuid}`,
          createInquiryRequest
        )
          .then((response: MappedInquiryView) => {
            dispatch(receivedLastInquiryToStorefront(response));
            return response;
          })
          .catch((error) => {
            const errorId = LogService.log(`Inquiry error: ${error.message}`, {
              inquiry: createInquiryRequest,
              response: error.response,
            });

            Logger.error(error.message, error);

            throw new Error(`Something went wrong—please try again later. (${errorId})`);
          });
      }

      dispatch(showExistingInquiryError(existingInquiry));
      throw new Error(ALREADY_INQUIRED_MESSAGE);
    });
}

export const bulkCreateInquiry = (
  bulkCreateInquiryRequest: BulkCreateInquiryRequest
): Promise<MappedInquiryView[]> =>
  ApiService.post('/web-marketplace-api/v1/inquiry/bulk', bulkCreateInquiryRequest);
